/**
 * SettingsLayout
 *
 * Route-based settings layout using FloatingNav horizontal navigation.
 * Provides floating navigation bar with categorized settings sections.
 *
 * Desktop: horizontal floating nav bar with hover-reveal dropdowns.
 * Mobile: scrollable horizontal tab strip.
 */

import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FloatingNav,
  type FloatingNavCategory,
  type FloatingNavItem,
} from "@/components/layout/FloatingNav";
import { AppLayout } from "@/components/layout/AppLayout";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Building2,
  Users,
  Settings as SettingsIcon,
  Shield,
  History,
  Church,
  MapPin,
  Database,
  ListChecks,
  Puzzle,
  SlidersHorizontal,
  Tag,
  ToggleRight,
  Workflow,
  Timer,
  CreditCard,
  Palette,
  Lock,
  Sparkles,
  Baby,
} from "lucide-react";

/**
 * @param isOrgAdmin  Whether the viewer is an org admin OR platform super-admin
 *   (the broad admin gate used for most Features items, kept for back-compat).
 * @param isOrgAdminStrict  Whether the viewer is specifically an org admin.
 *   Used to hide org-admin-only items (e.g. Voice profile) from super-admins who
 *   lack org-admin membership, since their writes fail the org-admin-only RLS.
 */
function buildCategories(
  isOrgAdmin: boolean,
  isOrgAdminStrict: boolean,
): FloatingNavCategory[] {
  const categories: FloatingNavCategory[] = [];

  if (isOrgAdmin) {
    const orgItems: FloatingNavItem[] = [
      {
        id: "organization",
        label: "Organization",
        icon: Building2,
        description: "Name, logo, branding",
      },
      {
        id: "team",
        label: "Team",
        icon: Users,
        description: "Members & roles",
      },
    ];

    orgItems.push(
      {
        id: "ministries",
        label: "Ministry Areas",
        icon: Church,
        description: "Departments for access & reporting",
      },
      {
        id: "locations",
        label: "Locations",
        icon: MapPin,
        description: "Campuses & facilities",
      },
    );

    if (isOrgAdminStrict) {
      orgItems.push({
        id: "directory",
        label: "Directory & Invites",
        icon: Users,
        description: "Directory access and invitations",
      });
    }

    categories.push({ group: "Organization", groupIcon: Building2, menuHint: "Menu", items: orgItems });
  }

  // ── Billing (admin only) ─────────────────────────────────────
  if (isOrgAdmin) {
    categories.push({
      group: "Finance & Billing",
      groupIcon: CreditCard,
      menuHint: "Menu",
      items: [
        {
          id: "finance-settings",
          label: "Finance setup",
          icon: CreditCard,
          description: "Fiscal year, contribution defaults & approvals",
        },
        {
          id: "billing",
          label: "Subscription",
          icon: CreditCard,
          description: "Plan & billing",
        },
      ],
    });
  }

  // ── Feature controls and connected services (admin only) ─────
  if (isOrgAdmin) {
    const featureItems: FloatingNavItem[] = [
      {
        id: "modules",
        label: "Modules",
        icon: ToggleRight,
        description: "Enable/disable features",
      },
    ];

    // Voice profile writes are org-admin-only at the RLS layer, so don't show
    // it to super-admins who aren't org admins of the selected org.
    if (isOrgAdminStrict) {
      featureItems.push({
        id: "voice",
        label: "Voice profile",
        icon: Sparkles,
        description: "Teach Solomon your church's tone",
      },
      {
        id: "integrations",
        label: "Integrations",
        icon: Puzzle,
        description: "Connected services and sync health",
      });
    }

    categories.push({
      group: "Features & Integrations",
      groupIcon: ToggleRight,
      menuHint: "Menu",
      items: featureItems,
    });

    categories.push({
      group: "Security & Access",
      groupIcon: Shield,
      menuHint: "Menu",
      items: [
        {
          id: "permissions",
          label: "Permissions",
          icon: Shield,
          description: "Access control",
        },
        {
          id: "security",
          label: "Security",
          icon: Lock,
          description: "MFA & session policy",
        },
        {
          id: "kids-checkin",
          label: "Children's check-in",
          icon: Baby,
          description: "Pickup security policy",
        },
        {
          id: "audit",
          label: "Audit Log",
          icon: History,
          description: "Activity history",
        },
      ],
    });

    categories.push({
      group: "Automation",
      groupIcon: Workflow,
      menuHint: "Menu",
      items: [
        {
          id: "workflows",
          label: "Workflows",
          icon: Workflow,
          description: "Automation settings",
        },
        {
          id: "today",
          label: "Today queue",
          icon: Timer,
          description: "SLA windows per role",
        },
      ],
    });

    const dataItems: FloatingNavItem[] = [
      {
        id: "import",
        label: "Import Data",
        icon: Database,
        description: "Bulk data import",
      },
      {
        id: "option-lists",
        label: "Option Lists",
        icon: ListChecks,
        description: "Custom dropdowns",
      },
      {
        id: "custom-fields",
        label: "Custom Fields",
        icon: SlidersHorizontal,
        description: "Person data fields",
      },
      {
        id: "tags",
        label: "Tags",
        icon: Tag,
        description: "Organize & segment people",
      },
      {
        id: "widgets",
        label: "Widgets",
        icon: Puzzle,
        description: "Dashboard widgets",
      },
    ];

    categories.push({
      group: "Data & Configuration",
      groupIcon: Database,
      menuHint: "Menu",
      items: dataItems,
    });
  }

  // ── Personal settings (available to every authenticated member) ─
  const personalItems: FloatingNavItem[] = [
    {
      id: "preferences",
      label: "My preferences",
      icon: SettingsIcon,
      description: "Appearance, defaults & shortcuts",
    },
  ];

  categories.push({
    group: "My Settings",
    groupIcon: Palette,
    menuHint: "Menu",
    items: personalItems,
  });

  return categories;
}

interface SettingsLayoutProps {
  children: React.ReactNode;
}

function sectionIdFromPath(pathname: string): string {
  const segments = pathname.split("/");
  return segments[segments.length - 1] || "organization";
}

export function SettingsLayout({ children }: SettingsLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOrgAdmin, isSuperAdmin } = usePermissions();
  const isAdmin = isOrgAdmin || isSuperAdmin;

  const categories = useMemo(
    () => buildCategories(isAdmin, isOrgAdmin),
    [isAdmin, isOrgAdmin],
  );
  const activeSection = useMemo(() => {
    const id = sectionIdFromPath(location.pathname);
    // Notifications remains a focused deep link, but its controls live under
    // My preferences, so keep the personal settings category active for it.
    const navigationSection = id === "notifications" ? "preferences" : id;
    // Verify the section exists in categories
    for (const cat of categories) {
      if (cat.items.some((item) => item.id === navigationSection)) {
        return navigationSection;
      }
    }
    return "organization";
  }, [location.pathname, categories]);

  const handleNavigate = (id: string) => {
    if (id === "finance-settings") {
      navigate("/finance/settings/accounting-setup");
      return;
    }

    navigate(`/settings/${id}`);
  };

  // Scroll to a settings deep-link's hash target (e.g. /settings/kids-checkin#bg-check-vendor).
  // React Router does not auto-scroll to hash fragments, and the section content mounts after
  // navigation, so a one-frame defer lets the target element render before we scroll to it.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const raf = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [location.pathname, location.hash]);

  return (
    <AppLayout>
      <div className="space-y-6 p-4 md:p-6">
        <FloatingNav
          categories={categories}
          activeSection={activeSection}
          onNavigate={handleNavigate}
          className="floating-nav-settings"
        />
        {children}
      </div>
    </AppLayout>
  );
}
